/**
 * ParcelsTab Component
 * 
 * Displays parcel boundaries and neighbor information for a property.
 * Automatically links property to parcel data from NC OneMap on first load.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, Pressable, ActivityIndicator, ScrollView, Platform, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { PropertyDetail } from '../../shared/types/property';
import { GeoFeature, NeighborsByTier, GeoFeatureSummary } from '../../shared/types/parcel';
import { authApi, PropertyFeaturesResponse, LinkParcelResponse } from '../../shared/utils/api';
import { PropertyMap } from '../map';
import { ParcelOverlay, NeighborsList, ParcelLegend, getTierColor, getPricePerAcreColor, PRICE_PER_ACRE_BUCKETS, NeighborhoodAnalytics } from '../map';
import { Card, Button } from '../ui';
import { clusterOwners, ParcelOwnerData, OwnerCluster } from '../../shared/utils/ownerMatching';

// ============================================
// Types
// ============================================

interface ParcelsTabProps {
  property: PropertyDetail;
  portfolioId: number;
}

interface ParcelData {
  primaryParcel: GeoFeature | null;
  neighborsByTier: NeighborsByTier[];
  loading: boolean;
  error: string | null;
  hasLinkedParcel: boolean;
}

// ============================================
// CSV Export Utilities
// ============================================

// Size buckets for analysis
const SIZE_BUCKETS = {
  small: { label: 'Small (<5 ac)', min: 0, max: 5 },
  medium: { label: 'Medium (5-25 ac)', min: 5, max: 25 },
  large: { label: 'Large (25-100 ac)', min: 25, max: 100 },
  xlarge: { label: 'XLarge (100+ ac)', min: 100, max: Infinity },
} as const;

function getSizeBucket(acres: number): string {
  if (acres < 5) return 'small';
  if (acres < 25) return 'medium';
  if (acres < 100) return 'large';
  return 'xlarge';
}

function getPriceBucket(perAcre: number): string {
  if (perAcre < 2000) return '<$2k/ac';
  if (perAcre < 5000) return '$2-5k/ac';
  if (perAcre < 10000) return '$5-10k/ac';
  if (perAcre < 20000) return '$10-20k/ac';
  return '>$20k/ac';
}

function escapeCSV(value: string | number | null | undefined): string {
  if (value == null) return '';
  const str = String(value);
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

interface ExportData {
  property: PropertyDetail;
  primaryParcel: GeoFeature;
  neighborsByTier: NeighborsByTier[];
  ownerClusters: OwnerCluster[];
  askPrice: number | null;
}

function generateParcelExportCSV(data: ExportData): string {
  const { property, primaryParcel, neighborsByTier, ownerClusters, askPrice } = data;
  const lines: string[] = [];
  
  // Build owner cluster lookup
  const ownerToCluster = new Map<string, OwnerCluster>();
  for (const cluster of ownerClusters) {
    for (const member of cluster.members) {
      ownerToCluster.set(member.rawName, cluster);
      ownerToCluster.set(member.normalizedName, cluster);
    }
  }
  
  // Collect all parcels for analysis
  const allParcels: Array<{
    isSubject: boolean;
    tier: number;
    parcelId: string;
    ownerName: string;
    familyGroup: string;
    ownerType: string;
    siteAddress: string;
    county: string;
    acres: number;
    totalValue: number;
    landValue: number;
    buildingValue: number;
    totalPerAcre: number;
    landPerAcre: number;
    sizeBucket: string;
    priceBucket: string;
    hasStructures: boolean;
  }> = [];
  
  // Add primary parcel
  const primaryAcres = Number(primaryParcel.area_acres) || 0;
  const primaryTotal = Number(primaryParcel.total_value) || 0;
  const primaryLand = Number(primaryParcel.land_value) || 0;
  const primaryBuilding = Number(primaryParcel.building_value) || 0;
  const primaryTotalPerAcre = primaryAcres > 0 ? primaryTotal / primaryAcres : 0;
  const primaryLandPerAcre = primaryAcres > 0 ? primaryLand / primaryAcres : 0;
  const primaryCluster = ownerToCluster.get(primaryParcel.owner_name || '');
  
  allParcels.push({
    isSubject: true,
    tier: 0,
    parcelId: primaryParcel.parcel_id || String(primaryParcel.id),
    ownerName: primaryParcel.owner_name || '',
    familyGroup: primaryCluster?.representativeName || primaryParcel.owner_name || '',
    ownerType: primaryCluster?.clusterType || 'individual',
    siteAddress: primaryParcel.site_address || '',
    county: primaryParcel.county_name || property.county || '',
    acres: primaryAcres,
    totalValue: primaryTotal,
    landValue: primaryLand,
    buildingValue: primaryBuilding,
    totalPerAcre: primaryTotalPerAcre,
    landPerAcre: primaryLandPerAcre,
    sizeBucket: getSizeBucket(primaryAcres),
    priceBucket: getPriceBucket(primaryTotalPerAcre),
    hasStructures: primaryBuilding > 10000 || (primaryBuilding > 0 && primaryBuilding / primaryTotal > 0.15),
  });
  
  // Add neighbors
  for (const tierData of neighborsByTier) {
    for (const neighborWrapper of tierData.neighbors) {
      const neighbor = (neighborWrapper as any).neighbor || neighborWrapper;
      const acres = Number(neighbor.area_acres) || 0;
      const totalValue = Number(neighbor.total_value) || 0;
      const landValue = Number(neighbor.land_value) || 0;
      const buildingValue = Number(neighbor.building_value) || 0;
      const totalPerAcre = acres > 0 ? totalValue / acres : 0;
      const landPerAcre = acres > 0 ? landValue / acres : 0;
      const cluster = ownerToCluster.get(neighbor.owner_name || '');
      
      allParcels.push({
        isSubject: false,
        tier: tierData.tier,
        parcelId: neighbor.parcel_id || String(neighbor.id),
        ownerName: neighbor.owner_name || '',
        familyGroup: cluster?.representativeName || neighbor.owner_name || '',
        ownerType: cluster?.clusterType || 'individual',
        siteAddress: neighbor.site_address || '',
        county: neighbor.county_name || '',
        acres,
        totalValue,
        landValue,
        buildingValue,
        totalPerAcre,
        landPerAcre,
        sizeBucket: getSizeBucket(acres),
        priceBucket: getPriceBucket(totalPerAcre),
        hasStructures: buildingValue > 10000 || (buildingValue > 0 && totalValue > 0 && buildingValue / totalValue > 0.15),
      });
    }
  }
  
  // Calculate summary statistics
  const neighbors = allParcels.filter(p => !p.isSubject);
  const totalAcres = neighbors.reduce((sum, p) => sum + p.acres, 0);
  const totalValue = neighbors.reduce((sum, p) => sum + p.totalValue, 0);
  const allPerAcre = neighbors.map(p => p.totalPerAcre).filter(v => v > 0);
  const medianPerAcre = median(allPerAcre);
  const avgPerAcre = allPerAcre.length > 0 ? allPerAcre.reduce((a, b) => a + b, 0) / allPerAcre.length : 0;
  
  const withStructures = neighbors.filter(p => p.hasStructures);
  const landOnly = neighbors.filter(p => !p.hasStructures);
  
  const familyClusters = ownerClusters.filter(c => c.clusterType === 'family');
  const entityClusters = ownerClusters.filter(c => c.clusterType === 'entity');
  const individualClusters = ownerClusters.filter(c => c.clusterType === 'same_person');
  
  // Tier counts
  const tierCounts: Record<number, number> = {};
  for (const p of neighbors) {
    tierCounts[p.tier] = (tierCounts[p.tier] || 0) + 1;
  }
  
  // Size bucket counts
  const sizeBucketCounts: Record<string, { count: number; acres: number; medianPerAcre: number }> = {};
  for (const key of Object.keys(SIZE_BUCKETS)) {
    const bucketed = neighbors.filter(p => p.sizeBucket === key);
    sizeBucketCounts[key] = {
      count: bucketed.length,
      acres: bucketed.reduce((sum, p) => sum + p.acres, 0),
      medianPerAcre: median(bucketed.map(p => p.totalPerAcre).filter(v => v > 0)),
    };
  }
  
  // =========================================
  // Build CSV
  // =========================================
  
  // Header section
  lines.push('# PARCEL DATA EXPORT');
  lines.push(`# Generated: ${new Date().toISOString()}`);
  lines.push('#');
  lines.push('');
  
  // Subject Property Info
  lines.push('## SUBJECT PROPERTY');
  lines.push(`Property Name,${escapeCSV(property.street_address || property.address || `Property ${property.id}`)}`);
  lines.push(`Owner,${escapeCSV(primaryParcel.owner_name)}`);
  lines.push(`Address,${escapeCSV(primaryParcel.site_address)}`);
  lines.push(`County,${escapeCSV(primaryParcel.county_name || property.county)}`);
  lines.push(`Parcel ID,${escapeCSV(primaryParcel.parcel_id)}`);
  lines.push(`Acres,${primaryAcres.toFixed(2)}`);
  lines.push(`Total Value,$${primaryTotal.toLocaleString()}`);
  lines.push(`Land Value,$${primaryLand.toLocaleString()}`);
  lines.push(`Building Value,$${primaryBuilding.toLocaleString()}`);
  lines.push(`Total $/Acre,$${Math.round(primaryTotalPerAcre).toLocaleString()}`);
  lines.push(`Land $/Acre,$${Math.round(primaryLandPerAcre).toLocaleString()}`);
  lines.push(`Size Bucket,${SIZE_BUCKETS[primaryParcel.area_acres ? getSizeBucket(Number(primaryParcel.area_acres)) as keyof typeof SIZE_BUCKETS : 'small'].label}`);
  if (askPrice) {
    const askPerAcre = primaryAcres > 0 ? askPrice / primaryAcres : 0;
    lines.push(`Ask Price,$${askPrice.toLocaleString()}`);
    lines.push(`Ask $/Acre,$${Math.round(askPerAcre).toLocaleString()}`);
    const premiumPct = primaryTotal > 0 ? ((askPrice - primaryTotal) / primaryTotal * 100) : 0;
    lines.push(`Ask vs Tax Value,${premiumPct >= 0 ? '+' : ''}${premiumPct.toFixed(1)}%`);
  }
  lines.push('');
  
  // Neighborhood Summary
  lines.push('## NEIGHBORHOOD SUMMARY');
  lines.push(`Total Neighbors,${neighbors.length}`);
  lines.push(`Total Acres,${totalAcres.toFixed(2)}`);
  lines.push(`Total Value,$${totalValue.toLocaleString()}`);
  lines.push(`Unique Owners,${ownerClusters.length}`);
  lines.push(`Median $/Acre,$${Math.round(medianPerAcre).toLocaleString()}`);
  lines.push(`Average $/Acre,$${Math.round(avgPerAcre).toLocaleString()}`);
  lines.push(`Min $/Acre,$${Math.round(Math.min(...allPerAcre)).toLocaleString()}`);
  lines.push(`Max $/Acre,$${Math.round(Math.max(...allPerAcre)).toLocaleString()}`);
  lines.push(`With Structures,${withStructures.length}`);
  lines.push(`Land Only,${landOnly.length}`);
  lines.push('');
  
  // Tier breakdown
  lines.push('## BY TIER');
  lines.push('Tier,Count,Acres');
  for (const tier of Object.keys(tierCounts).sort((a, b) => Number(a) - Number(b))) {
    const tierParcels = neighbors.filter(p => p.tier === Number(tier));
    const tierAcres = tierParcels.reduce((sum, p) => sum + p.acres, 0);
    lines.push(`Tier ${tier},${tierCounts[Number(tier)]},${tierAcres.toFixed(2)}`);
  }
  lines.push('');
  
  // Size bucket breakdown
  lines.push('## BY SIZE BUCKET');
  lines.push('Size Bucket,Count,Acres,Median $/Acre');
  for (const [key, bucket] of Object.entries(SIZE_BUCKETS)) {
    const stats = sizeBucketCounts[key];
    lines.push(`${bucket.label},${stats.count},${stats.acres.toFixed(2)},$${Math.round(stats.medianPerAcre).toLocaleString()}`);
  }
  lines.push('');
  
  // Owner type breakdown
  lines.push('## BY OWNER TYPE');
  lines.push('Type,Count,Parcels,Acres');
  lines.push(`Families,${familyClusters.length},${familyClusters.reduce((sum, c) => sum + c.totalParcels, 0)},${familyClusters.reduce((sum, c) => sum + c.totalAcres, 0).toFixed(2)}`);
  lines.push(`Entities/Trusts,${entityClusters.length},${entityClusters.reduce((sum, c) => sum + c.totalParcels, 0)},${entityClusters.reduce((sum, c) => sum + c.totalAcres, 0).toFixed(2)}`);
  lines.push(`Individuals,${individualClusters.length},${individualClusters.reduce((sum, c) => sum + c.totalParcels, 0)},${individualClusters.reduce((sum, c) => sum + c.totalAcres, 0).toFixed(2)}`);
  lines.push('');
  
  // Top owners
  lines.push('## TOP OWNERS BY ACREAGE');
  lines.push('Rank,Owner/Family,Type,Parcels,Acres,Total Value,$/Acre');
  const topOwners = [...ownerClusters].sort((a, b) => b.totalAcres - a.totalAcres).slice(0, 10);
  topOwners.forEach((owner, index) => {
    const perAcre = owner.totalAcres > 0 ? owner.totalValue / owner.totalAcres : 0;
    lines.push(`${index + 1},${escapeCSV(owner.representativeName)},${owner.clusterType},${owner.totalParcels},${owner.totalAcres.toFixed(2)},$${owner.totalValue.toLocaleString()},$${Math.round(perAcre).toLocaleString()}`);
  });
  lines.push('');
  
  // Data section header
  lines.push('## PARCEL DATA');
  lines.push('');
  
  // Column headers
  const headers = [
    'is_subject',
    'tier',
    'parcel_id',
    'owner_name',
    'family_group',
    'owner_type',
    'site_address',
    'county',
    'acres',
    'total_value',
    'land_value',
    'building_value',
    'total_per_acre',
    'land_per_acre',
    'size_bucket',
    'price_bucket',
    'has_structures',
  ];
  lines.push(headers.join(','));
  
  // Data rows
  for (const parcel of allParcels) {
    const row = [
      parcel.isSubject ? '1' : '0',
      parcel.tier.toString(),
      escapeCSV(parcel.parcelId),
      escapeCSV(parcel.ownerName),
      escapeCSV(parcel.familyGroup),
      parcel.ownerType,
      escapeCSV(parcel.siteAddress),
      escapeCSV(parcel.county),
      parcel.acres.toFixed(2),
      parcel.totalValue.toFixed(0),
      parcel.landValue.toFixed(0),
      parcel.buildingValue.toFixed(0),
      parcel.totalPerAcre.toFixed(2),
      parcel.landPerAcre.toFixed(2),
      parcel.sizeBucket,
      parcel.priceBucket,
      parcel.hasStructures ? '1' : '0',
    ];
    lines.push(row.join(','));
  }
  
  return lines.join('\n');
}

function downloadCSV(content: string, filename: string) {
  if (Platform.OS === 'web') {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else {
    // For native, we'd need to use expo-file-system and expo-sharing
    Alert.alert('Export', 'CSV export is only supported on web currently.');
  }
}

// ============================================
// Value Analysis Card Component
// ============================================

interface ValueAnalysisCardProps {
  property: PropertyDetail;
  parcel: GeoFeature;
  isMobile: boolean;
}

function ValueAnalysisCard({ property, parcel, isMobile }: ValueAnalysisCardProps) {
  const styles = useStyles();
  
  // Get values
  const askPrice = property.property_value ? Number(property.property_value) : null;
  const taxValue = parcel.total_value ? Number(parcel.total_value) : null;
  const landValue = parcel.land_value ? Number(parcel.land_value) : null;
  const buildingValue = parcel.building_value ? Number(parcel.building_value) : null;
  const acres = parcel.area_acres ? Number(parcel.area_acres) : null;
  
  // Calculate delta
  const hasBothValues = askPrice != null && taxValue != null && askPrice > 0 && taxValue > 0;
  const valueDelta = hasBothValues ? askPrice - taxValue : null;
  const deltaPercent = hasBothValues ? ((askPrice - taxValue) / taxValue) * 100 : null;
  const isUndervalued = valueDelta != null && valueDelta < 0; // Ask price < tax value = potential deal
  const isOvervalued = valueDelta != null && valueDelta > 0;
  
  // Calculate per-acre costs
  const askPerAcre = askPrice != null && acres != null && acres > 0 ? askPrice / acres : null;
  const taxPerAcre = taxValue != null && acres != null && acres > 0 ? taxValue / acres : null;
  const landPerAcre = landValue != null && acres != null && acres > 0 ? landValue / acres : null;
  
  // Format helpers
  const formatCurrency = (val: number | null) => 
    val != null ? `$${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : 'N/A';
  const formatPerAcre = (val: number | null) => 
    val != null ? `$${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}/ac` : 'N/A';
  const formatPercent = (val: number | null) => 
    val != null ? `${val >= 0 ? '+' : ''}${val.toFixed(1)}%` : 'N/A';

  return (
    <Card style={{ padding: styles.utils.spacing.lg }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm, marginBottom: styles.utils.spacing.md }}>
        <Ionicons name="analytics-outline" size={24} color={styles.tokens.color.primary} />
        <Text style={[styles.heading('lg')]}>
          Value Analysis
        </Text>
      </View>

      {/* Main Value Comparison */}
      <View style={{ 
        flexDirection: isMobile ? 'column' : 'row',
        gap: styles.utils.spacing.lg,
        marginBottom: styles.utils.spacing.lg,
      }}>
        {/* Ask Price */}
        <View style={{ 
          flex: 1, 
          backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.08),
          padding: styles.utils.spacing.md,
          borderRadius: styles.utils.radius.md,
          borderLeftWidth: 4,
          borderLeftColor: styles.tokens.color.primary,
        }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: 4 }]}>
            📊 Ask Price
          </Text>
          <Text style={[styles.heading('xl'), { color: styles.tokens.color.primary }]}>
            {formatCurrency(askPrice)}
          </Text>
          {askPerAcre != null && (
            <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7), marginTop: 4 }]}>
              {formatPerAcre(askPerAcre)}
            </Text>
          )}
        </View>

        {/* Tax Assessed Value */}
        <View style={{ 
          flex: 1, 
          backgroundColor: addColorOpacity(styles.tokens.color.secondary || '#6B7280', 0.08),
          padding: styles.utils.spacing.md,
          borderRadius: styles.utils.radius.md,
          borderLeftWidth: 4,
          borderLeftColor: styles.tokens.color.secondary || '#6B7280',
        }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: 4 }]}>
            🏛️ Tax Assessed Value
          </Text>
          <Text style={[styles.heading('xl'), { color: styles.tokens.color.secondary || '#6B7280' }]}>
            {formatCurrency(taxValue)}
          </Text>
          {taxPerAcre != null && (
            <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7), marginTop: 4 }]}>
              {formatPerAcre(taxPerAcre)}
            </Text>
          )}
        </View>

        {/* Delta */}
        {hasBothValues && (
          <View style={{ 
            flex: 1, 
            backgroundColor: addColorOpacity(
              isUndervalued ? styles.tokens.color.success : styles.tokens.color.warning,
              0.1
            ),
            padding: styles.utils.spacing.md,
            borderRadius: styles.utils.radius.md,
            borderLeftWidth: 4,
            borderLeftColor: isUndervalued ? styles.tokens.color.success : styles.tokens.color.warning,
          }}>
            <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: 4 }]}>
              {isUndervalued ? '✨ Potential Upside' : '⚠️ Premium Over Tax'}
            </Text>
            <Text style={[styles.heading('xl'), { 
              color: isUndervalued ? styles.tokens.color.success : styles.tokens.color.warning 
            }]}>
              {formatCurrency(Math.abs(valueDelta!))}
            </Text>
            <Text style={[styles.text('sm'), { 
              color: isUndervalued ? styles.tokens.color.success : styles.tokens.color.warning,
              fontWeight: styles.utils.typography.weights.semibold,
              marginTop: 4,
            }]}>
              {formatPercent(deltaPercent)} {isUndervalued ? 'below' : 'above'} tax value
            </Text>
          </View>
        )}
      </View>

      {/* Detailed Breakdown */}
      {(landValue != null || buildingValue != null) && (
        <View style={{
          padding: styles.utils.spacing.md,
          backgroundColor: addColorOpacity(styles.tokens.color.surface, 0.5),
          borderRadius: styles.utils.radius.md,
          borderWidth: 1,
          borderColor: styles.tokens.color.border,
        }}>
          <Text style={[styles.text('sm'), { 
            fontWeight: styles.utils.typography.weights.semibold, 
            marginBottom: styles.utils.spacing.sm,
            color: addColorOpacity(styles.tokens.color.onBackground, 0.8),
          }]}>
            Tax Assessment Breakdown
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: styles.utils.spacing.lg }}>
            {landValue != null && (
              <View>
                <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                  Land Value
                </Text>
                <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                  {formatCurrency(landValue)}
                </Text>
                {landPerAcre != null && (
                  <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                    {formatPerAcre(landPerAcre)}
                  </Text>
                )}
              </View>
            )}
            {buildingValue != null && (
              <View>
                <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                  Building Value
                </Text>
                <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                  {formatCurrency(buildingValue)}
                </Text>
              </View>
            )}
            {acres != null && (
              <View>
                <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                  Total Acres
                </Text>
                <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                  {acres.toFixed(2)} ac
                </Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* No Ask Price Warning */}
      {askPrice == null && taxValue != null && (
        <View style={{
          padding: styles.utils.spacing.sm,
          backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.1),
          borderRadius: styles.utils.radius.sm,
          flexDirection: 'row',
          alignItems: 'center',
          gap: styles.utils.spacing.sm,
          marginTop: styles.utils.spacing.sm,
        }}>
          <Ionicons name="information-circle-outline" size={16} color={styles.tokens.color.primary} />
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7), flex: 1 }]}>
            Add an ask price in property details to see value comparison and investment analysis.
          </Text>
        </View>
      )}
    </Card>
  );
}

// ============================================
// Main Component
// ============================================

export function ParcelsTab({ property, portfolioId }: ParcelsTabProps) {
  const styles = useStyles();
  const { isMobile, isSmallMobile } = useResponsive();

  // State
  const [parcelData, setParcelData] = useState<ParcelData>({
    primaryParcel: null,
    neighborsByTier: [],
    loading: true,
    error: null,
    hasLinkedParcel: false,
  });
  const [selectedParcelId, setSelectedParcelId] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentMaxTier, setCurrentMaxTier] = useState(2);
  const [expandingTiers, setExpandingTiers] = useState(false);
  const [selectedOwnerName, setSelectedOwnerName] = useState<string | null>(null);
  const [parcelColorMode, setParcelColorMode] = useState<'tier' | 'owner' | 'value'>('tier');

  // Compute owner colors and clusters from neighbor data
  const { ownerColors, ownerClusters } = useMemo(() => {
    const colorMap = new Map<string, string>();
    
    if (parcelData.neighborsByTier.length === 0) {
      return { ownerColors: colorMap, ownerClusters: [] as OwnerCluster[] };
    }
    
    // Extract parcel data for clustering
    const parcelDataForClustering: ParcelOwnerData[] = [];
    
    for (const tierData of parcelData.neighborsByTier) {
      for (const neighborEntry of tierData.neighbors) {
        const n = (neighborEntry as any).neighbor || neighborEntry;
        const acres = Number(n.area_acres) || 0;
        const totalValue = Number(n.total_value) || 0;
        
        if (n.owner_name && acres > 0) {
          parcelDataForClustering.push({
            parcelId: n.parcel_id || `${n.id}`,
            ownerName: n.owner_name,
            acres,
            value: totalValue,
            tier: tierData.tier,
          });
        }
      }
    }
    
    // Cluster owners and extract colors
    const clusters = clusterOwners(parcelDataForClustering, 0.85);
    
    for (const cluster of clusters) {
      for (const member of cluster.members) {
        colorMap.set(member.rawName, cluster.color);
        colorMap.set(member.normalizedName, cluster.color);
      }
    }
    
    return { ownerColors: colorMap, ownerClusters: clusters };
  }, [parcelData.neighborsByTier]);

  // Load parcel data
  const loadParcelData = useCallback(async (maxTier: number = currentMaxTier) => {
    try {
      setParcelData(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await authApi.getPropertyFeatures(property.id, {
        include_neighbors: true,
        max_tier: maxTier,
      });

      if (response.success && response.features && response.features.length > 0) {
        // Find primary parcel
        const primaryFeature = response.features.find(
          (f: any) => f.association_type === 'primary' || f.neighbor_tier === 0
        );

        setParcelData({
          primaryParcel: primaryFeature || response.features[0],
          neighborsByTier: response.neighborsByTier || [],
          loading: false,
          error: null,
          hasLinkedParcel: true,
        });
      } else {
        // No linked parcel yet
        setParcelData({
          primaryParcel: null,
          neighborsByTier: [],
          loading: false,
          error: null,
          hasLinkedParcel: false,
        });
      }
    } catch (error: any) {
      console.error('Failed to load parcel data:', error);
      setParcelData(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load parcel data',
      }));
    }
  }, [property.id]);

  // Auto-link property to parcel
  const handleAutoLink = useCallback(async () => {
    if (!property.latitude || !property.longitude) {
      setParcelData(prev => ({
        ...prev,
        error: 'Property must have coordinates to link to parcel data.',
      }));
      return;
    }

    try {
      setLinking(true);
      
      const response = await authApi.linkPropertyToParcel(property.id, {
        auto_link: true,
        include_neighbors: true,
        max_tier: 2,
      });

      if (response.success) {
        // Reload the parcel data
        await loadParcelData();
      } else {
        setParcelData(prev => ({
          ...prev,
          error: response.message || 'Failed to link property to parcel.',
        }));
      }
    } catch (error: any) {
      console.error('Failed to auto-link parcel:', error);
      const errorMessage = error?.message || error?.data?.message || error?.data?.error || String(error) || 'Failed to link property to parcel.';
      setParcelData(prev => ({
        ...prev,
        error: errorMessage,
      }));
    } finally {
      setLinking(false);
    }
  }, [property.id, property.latitude, property.longitude, loadParcelData]);

  // Refresh parcel data
  const handleRefresh = useCallback(async (e?: any) => {
    // Prevent any default behavior that might cause page refresh
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    
    try {
      setRefreshing(true);
      setParcelData(prev => ({ ...prev, error: null })); // Clear any previous errors
      
      console.log('Starting parcel refresh for property:', property.id);
      
      const response = await authApi.refreshPropertyParcels(property.id, {
        include_neighbors: true,
        max_tier: 2,
      });

      console.log('Refresh response:', response);

      if (response.success) {
        // Reload the data to get updated neighbors
        await loadParcelData();
      } else {
        setParcelData(prev => ({
          ...prev,
          error: response.message || 'Failed to refresh parcel data',
        }));
      }
    } catch (error: any) {
      console.error('Failed to refresh parcel data:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to refresh parcel data. Please try again.';
      setParcelData(prev => ({
        ...prev,
        error: errorMessage,
      }));
    } finally {
      setRefreshing(false);
    }
  }, [property.id, loadParcelData]);

  // Handle neighbor selection
  const handleNeighborSelect = useCallback((neighbor: GeoFeatureSummary, tier: number) => {
    setSelectedParcelId(prev => prev === neighbor.id ? null : neighbor.id);
    // Also select the owner
    if (neighbor.owner_name) {
      const ownerName = neighbor.owner_name;
      setSelectedOwnerName(prev => prev === ownerName ? null : ownerName);
    }
  }, []);

  // Handle owner selection from analytics
  const handleOwnerSelect = useCallback((ownerName: string, color: string) => {
    setSelectedOwnerName(prev => prev === ownerName ? null : ownerName);
    setSelectedParcelId(null); // Clear individual parcel selection
    // Update color mode when an owner is selected
    setParcelColorMode(selectedOwnerName === ownerName ? 'tier' : 'owner');
  }, [selectedOwnerName]);

  // Expand to fetch more tiers
  const handleExpandTiers = useCallback(async () => {
    try {
      setExpandingTiers(true);
      const newMaxTier = currentMaxTier + 2; // Fetch 2 more tiers at a time
      
      console.log(`Expanding from tier ${currentMaxTier} to tier ${newMaxTier}`);
      
      const response = await authApi.refreshPropertyParcels(property.id, {
        include_neighbors: true,
        max_tier: newMaxTier,
      });
      
      if (response.success) {
        setCurrentMaxTier(newMaxTier);
        await loadParcelData(newMaxTier);
      } else {
        setParcelData(prev => ({
          ...prev,
          error: response.message || 'Failed to expand neighbor search',
        }));
      }
    } catch (error: any) {
      console.error('Failed to expand tiers:', error);
      setParcelData(prev => ({
        ...prev,
        error: error?.message || 'Failed to expand neighbor search',
      }));
    } finally {
      setExpandingTiers(false);
    }
  }, [property.id, currentMaxTier, loadParcelData]);

  // Handle CSV export
  const handleExport = useCallback(() => {
    if (!parcelData.primaryParcel) {
      Alert.alert('No Data', 'No parcel data available to export.');
      return;
    }
    
    try {
      const askPrice = property.property_value ? Number(property.property_value) : null;
      
      const csvContent = generateParcelExportCSV({
        property,
        primaryParcel: parcelData.primaryParcel,
        neighborsByTier: parcelData.neighborsByTier,
        ownerClusters,
        askPrice,
      });
      
      // Generate filename
      const propertyName = (property.street_address || property.address || `Property_${property.id}`)
        .replace(/[^a-zA-Z0-9]/g, '_')
        .substring(0, 30);
      const date = new Date().toISOString().split('T')[0];
      const filename = `Parcel_Data_${propertyName}_${date}.csv`;
      
      downloadCSV(csvContent, filename);
    } catch (error: any) {
      console.error('Export failed:', error);
      Alert.alert('Export Failed', error?.message || 'Failed to generate export file.');
    }
  }, [parcelData.primaryParcel, parcelData.neighborsByTier, ownerClusters, property]);

  // Initial load
  useEffect(() => {
    loadParcelData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id]);

  // Loading state
  if (parcelData.loading) {
    return (
      <View style={{ padding: styles.utils.spacing.xl, alignItems: 'center' }}>
        <ActivityIndicator size="large" color={styles.tokens.color.primary} />
        <Text style={[styles.text('base'), { marginTop: styles.utils.spacing.md, color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
          Loading parcel data...
        </Text>
      </View>
    );
  }

  // No parcel linked yet - show CTA
  if (!parcelData.hasLinkedParcel) {
    const hasCoordinates = property.latitude && property.longitude;
    const countySupported = property.state === 'NC'; // Currently only NC is supported

    return (
      <Card
        style={{
          padding: styles.utils.spacing.xl,
          alignItems: 'center',
          backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.05),
        }}
      >
        <View
          style={{
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.15),
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: styles.utils.spacing.lg,
          }}
        >
          <Ionicons name="map-outline" size={36} color={styles.tokens.color.primary} />
        </View>

        <Text style={[styles.heading('lg'), { textAlign: 'center', marginBottom: styles.utils.spacing.sm }]}>
          Parcel Data Available
        </Text>

        <Text style={[styles.text('base'), { textAlign: 'center', color: addColorOpacity(styles.tokens.color.onBackground, 0.7), marginBottom: styles.utils.spacing.lg, maxWidth: 400 }]}>
          Link this property to county parcel records to view property boundaries and discover neighboring landowners.
        </Text>

        {!hasCoordinates && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: styles.utils.spacing.sm,
              padding: styles.utils.spacing.md,
              backgroundColor: addColorOpacity(styles.tokens.color.warning, 0.1),
              borderRadius: styles.utils.radius.md,
              marginBottom: styles.utils.spacing.md,
            }}
          >
            <Ionicons name="warning-outline" size={20} color={styles.tokens.color.warning} />
            <Text style={[styles.text('sm'), { color: styles.tokens.color.warning, flex: 1 }]}>
              Add coordinates to this property first (edit property → Quick Address Lookup)
            </Text>
          </View>
        )}

        {!countySupported && hasCoordinates && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: styles.utils.spacing.sm,
              padding: styles.utils.spacing.md,
              backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.1),
              borderRadius: styles.utils.radius.md,
              marginBottom: styles.utils.spacing.md,
            }}
          >
            <Ionicons name="information-circle-outline" size={20} color={styles.tokens.color.primary} />
            <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.8), flex: 1 }]}>
              Parcel data is currently available for North Carolina properties only. More states coming soon!
            </Text>
          </View>
        )}

        <Button
          title={linking ? 'Linking...' : 'Link to Parcel Data'}
          onPress={handleAutoLink}
          disabled={linking || !hasCoordinates}
          variant="primary"
          size="lg"
        />

        {parcelData.error && (
          <Text style={[styles.text('sm'), { color: styles.tokens.color.error, marginTop: styles.utils.spacing.md, textAlign: 'center' }]}>
            {parcelData.error}
          </Text>
        )}

        {/* What you'll get */}
        <View style={{ marginTop: styles.utils.spacing.xl, width: '100%', maxWidth: 400 }}>
          <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold, marginBottom: styles.utils.spacing.md }]}>
            What you'll discover:
          </Text>
          
          {[
            { icon: 'resize-outline', text: 'Exact property boundaries' },
            { icon: 'people-outline', text: 'Neighboring landowner names' },
            { icon: 'cash-outline', text: 'Tax assessment values' },
            { icon: 'document-text-outline', text: 'Land use classifications' },
          ].map((item, index) => (
            <View
              key={index}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: styles.utils.spacing.sm,
                marginBottom: styles.utils.spacing.sm,
              }}
            >
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  backgroundColor: addColorOpacity(styles.tokens.color.success, 0.15),
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons name={item.icon as any} size={14} color={styles.tokens.color.success} />
              </View>
              <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.8) }]}>
                {item.text}
              </Text>
            </View>
          ))}
        </View>
      </Card>
    );
  }

  // Has parcel data - show map and neighbors
  return (
    <View style={{ gap: styles.utils.spacing.lg }}>
      {/* Map Controls Bar */}
      {parcelData.neighborsByTier.length > 0 && (
        <View style={{ 
          flexDirection: 'row', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: styles.utils.spacing.sm,
          marginBottom: styles.utils.spacing.sm,
        }}>
          {/* Color mode toggle */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.xs, flexWrap: 'wrap' }}>
            <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
              Color by:
            </Text>
            {(['tier', 'owner', 'value'] as const).map((mode) => (
              <Pressable
                key={mode}
                onPress={() => setParcelColorMode(mode)}
                style={{
                  paddingHorizontal: styles.utils.spacing.sm,
                  paddingVertical: 4,
                  borderRadius: styles.utils.radius.sm,
                  backgroundColor: parcelColorMode === mode ? styles.tokens.color.primary : styles.tokens.color.surface,
                  borderWidth: 1,
                  borderColor: parcelColorMode === mode ? styles.tokens.color.primary : styles.tokens.color.border,
                }}
              >
                <Text style={[styles.text('xs'), { 
                  color: parcelColorMode === mode ? styles.tokens.color.onPrimary : styles.tokens.color.onSurface,
                  fontWeight: parcelColorMode === mode ? styles.utils.typography.weights.semibold : styles.utils.typography.weights.regular,
                }]}>
                  {mode === 'tier' ? 'Tier' : mode === 'owner' ? 'Owner' : '$/Acre'}
                </Text>
              </Pressable>
            ))}
          </View>

          {/* Right side: Selected owner + Export button */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm }}>
            {/* Selected owner indicator / clear button */}
            {selectedOwnerName && (
              <Pressable
                onPress={() => {
                  setSelectedOwnerName(null);
                  setSelectedParcelId(null);
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  paddingHorizontal: styles.utils.spacing.sm,
                  paddingVertical: 4,
                  backgroundColor: ownerColors.get(selectedOwnerName) || styles.tokens.color.primary,
                  borderRadius: styles.utils.radius.sm,
                }}
              >
                <Text style={[styles.text('xs'), { color: '#FFF', fontWeight: styles.utils.typography.weights.medium }]} numberOfLines={1}>
                  {selectedOwnerName}
                </Text>
                <Ionicons name="close" size={14} color="#FFF" />
              </Pressable>
            )}
            
            {/* Export Button */}
            <Pressable
              onPress={handleExport}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: styles.utils.spacing.md,
                paddingVertical: 6,
                backgroundColor: pressed 
                  ? addColorOpacity('#10B981', 0.8)
                  : '#10B981',
                borderRadius: styles.utils.radius.sm,
              })}
            >
              <Ionicons name="download-outline" size={16} color="#FFF" />
              <Text style={[styles.text('xs'), { color: '#FFF', fontWeight: styles.utils.typography.weights.semibold }]}>
                Export CSV
              </Text>
            </Pressable>
          </View>
        </View>
      )}

      {/* Map with Parcel Overlay */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <PropertyMap
          property={property}
          height={isSmallMobile ? 350 : isMobile ? 400 : 500}
          showControls={true}
          showParcels={true}
          primaryParcel={parcelData.primaryParcel}
          neighborsByTier={parcelData.neighborsByTier}
          selectedParcelId={selectedParcelId}
          selectedOwnerName={selectedOwnerName}
          ownerColors={ownerColors}
          parcelColorMode={parcelColorMode}
          initialBasemap="satellite-streets"
          zoom={15}
        />
      </Card>

      {/* $/Acre Legend - shown when in value color mode */}
      {parcelColorMode === 'value' && (
        <View style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: styles.utils.spacing.sm,
          padding: styles.utils.spacing.sm,
          backgroundColor: styles.tokens.color.surface,
          borderRadius: styles.utils.radius.md,
          borderWidth: 1,
          borderColor: styles.tokens.color.border,
        }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginRight: styles.utils.spacing.xs }]}>
            $/Acre:
          </Text>
          {PRICE_PER_ACRE_BUCKETS.map((bucket, index) => (
            <View key={index} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <View style={{ width: 12, height: 12, backgroundColor: bucket.color, borderRadius: 2 }} />
              <Text style={[styles.text('xs'), { color: styles.tokens.color.onSurface }]}>
                {bucket.label}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Parcel Info Card */}
      {parcelData.primaryParcel && (
        <Card style={{ padding: styles.utils.spacing.lg }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: styles.utils.spacing.md }}>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm, marginBottom: styles.utils.spacing.xs }}>
                <View
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: getTierColor(0).fill,
                    borderWidth: 2,
                    borderColor: getTierColor(0).stroke,
                    borderRadius: 3,
                  }}
                />
                <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), textTransform: 'uppercase', letterSpacing: 0.5 }]}>
                  Subject Property
                </Text>
              </View>
              <Text style={[styles.heading('lg'), { marginBottom: styles.utils.spacing.xs }]}>
                {parcelData.primaryParcel.owner_name || 'Property Owner'}
              </Text>
              {parcelData.primaryParcel.site_address && (
                <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
                  {parcelData.primaryParcel.site_address}
                </Text>
              )}
            </View>
            
            <Pressable
              onPress={(e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                handleRefresh();
              }}
              disabled={refreshing}
              style={{
                padding: styles.utils.spacing.sm,
                backgroundColor: styles.tokens.color.surface,
                borderRadius: styles.utils.radius.md,
              }}
            >
              {refreshing ? (
                <ActivityIndicator size={20} color={styles.tokens.color.primary} />
              ) : (
                <Ionicons 
                  name="refresh-outline" 
                  size={20} 
                  color={styles.tokens.color.primary} 
                />
              )}
            </Pressable>
          </View>

          {/* Basic Parcel Info */}
          <View style={{ 
            flexDirection: 'row', 
            flexWrap: 'wrap',
            gap: styles.utils.spacing.md,
            paddingTop: styles.utils.spacing.md,
            borderTopWidth: 1,
            borderTopColor: styles.tokens.color.border,
          }}>
            <View style={{ minWidth: 100 }}>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: 2 }]}>
                Acreage
              </Text>
              <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                {parcelData.primaryParcel.area_acres != null 
                  ? `${Number(parcelData.primaryParcel.area_acres).toFixed(2)} ac`
                  : 'N/A'
                }
              </Text>
            </View>
            
            <View style={{ minWidth: 100 }}>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: 2 }]}>
                Tax Value
              </Text>
              <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.semibold, color: styles.tokens.color.primary }]}>
                {parcelData.primaryParcel.total_value != null 
                  ? `$${Number(parcelData.primaryParcel.total_value).toLocaleString()}`
                  : 'N/A'
                }
              </Text>
            </View>
            
            <View style={{ minWidth: 100 }}>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: 2 }]}>
                Land Use
              </Text>
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]} numberOfLines={1}>
                {parcelData.primaryParcel.land_use_description || 'N/A'}
              </Text>
            </View>
            
            {parcelData.primaryParcel.parcel_id && (
              <View style={{ minWidth: 120 }}>
                <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: 2 }]}>
                  Parcel ID
                </Text>
                <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]} numberOfLines={1}>
                  {parcelData.primaryParcel.parcel_id}
                </Text>
              </View>
            )}
          </View>
        </Card>
      )}

      {/* 💰 VALUE ANALYSIS CARD */}
      {parcelData.hasLinkedParcel && parcelData.primaryParcel && (
        <ValueAnalysisCard 
          property={property}
          parcel={parcelData.primaryParcel}
          isMobile={isMobile}
        />
      )}

      {/* 📊 NEIGHBORHOOD ANALYTICS - The Power Feature */}
      {parcelData.neighborsByTier.length > 0 && parcelData.primaryParcel && (
        <Card style={{ padding: styles.utils.spacing.lg }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm, marginBottom: styles.utils.spacing.sm }}>
            <Ionicons name="analytics" size={24} color={styles.tokens.color.primary} />
            <Text style={[styles.heading('lg')]}>
              Market Intelligence
            </Text>
          </View>
          <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginBottom: styles.utils.spacing.md }]}>
            Deep analysis of {parcelData.neighborsByTier.reduce((sum, tier) => sum + tier.neighbors.length, 0)} neighboring properties
          </Text>
          
          <NeighborhoodAnalytics
            neighborsByTier={parcelData.neighborsByTier}
            primaryParcel={parcelData.primaryParcel}
            userAskPrice={property.property_value ? Number(property.property_value) : null}
            onOwnerSelect={handleOwnerSelect}
            selectedOwner={selectedOwnerName}
          />
        </Card>
      )}

      {/* Neighbors List */}
      {parcelData.neighborsByTier.length > 0 ? (
        <Card style={{ padding: styles.utils.spacing.lg }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: styles.utils.spacing.md }}>
            <Text style={[styles.heading('lg')]}>
              Neighboring Properties
            </Text>
            <View style={{ 
              backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.1),
              paddingHorizontal: styles.utils.spacing.sm,
              paddingVertical: 4,
              borderRadius: styles.utils.radius.sm,
            }}>
              <Text style={[styles.text('xs'), { color: styles.tokens.color.primary, fontWeight: styles.utils.typography.weights.semibold }]}>
                Tier 1-{Math.max(...parcelData.neighborsByTier.map(t => t.tier))}
              </Text>
            </View>
          </View>
          
          <NeighborsList
            neighborsByTier={parcelData.neighborsByTier}
            onNeighborSelect={handleNeighborSelect}
            selectedNeighborId={selectedParcelId}
            showAnalytics={false}
            maxHeight={isMobile ? 400 : 500}
          />
          
          {/* Expand to more tiers button */}
          {currentMaxTier < 6 && (
            <View style={{ marginTop: styles.utils.spacing.lg, alignItems: 'center' }}>
              <Pressable
                onPress={(e) => {
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  handleExpandTiers();
                }}
                disabled={expandingTiers}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: styles.utils.spacing.sm,
                  paddingHorizontal: styles.utils.spacing.lg,
                  paddingVertical: styles.utils.spacing.md,
                  backgroundColor: expandingTiers 
                    ? addColorOpacity(styles.tokens.color.surface, 0.5)
                    : pressed 
                      ? addColorOpacity(styles.tokens.color.primary, 0.15)
                      : addColorOpacity(styles.tokens.color.primary, 0.1),
                  borderRadius: styles.utils.radius.md,
                  borderWidth: 1,
                  borderColor: addColorOpacity(styles.tokens.color.primary, 0.3),
                })}
              >
                {expandingTiers ? (
                  <ActivityIndicator size={16} color={styles.tokens.color.primary} />
                ) : (
                  <Ionicons name="expand-outline" size={18} color={styles.tokens.color.primary} />
                )}
                <Text style={[styles.text('sm'), { 
                  color: styles.tokens.color.primary,
                  fontWeight: styles.utils.typography.weights.medium,
                }]}>
                  {expandingTiers 
                    ? 'Expanding search...' 
                    : `Expand to Tier ${currentMaxTier + 1}-${Math.min(currentMaxTier + 2, 6)}`
                  }
                </Text>
              </Pressable>
              <Text style={[styles.text('xs'), { 
                color: addColorOpacity(styles.tokens.color.onBackground, 0.5),
                marginTop: styles.utils.spacing.xs,
              }]}>
                Discover more distant neighbors
              </Text>
            </View>
          )}
        </Card>
      ) : parcelData.hasLinkedParcel && (
        <Card style={{ padding: styles.utils.spacing.lg }}>
          <View style={{ alignItems: 'center', gap: styles.utils.spacing.md }}>
            <View style={{
              width: 60,
              height: 60,
              borderRadius: 30,
              backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.1),
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Ionicons name="people-outline" size={28} color={styles.tokens.color.primary} />
            </View>
            
            <Text style={[styles.heading('lg'), { textAlign: 'center' }]}>
              Neighboring Properties
            </Text>
            
            <Text style={[styles.text('sm'), { 
              textAlign: 'center', 
              color: addColorOpacity(styles.tokens.color.onBackground, 0.7),
              maxWidth: 300,
            }]}>
              Neighbor data hasn't been loaded yet. Tap refresh to discover adjacent landowners and see who owns property around yours.
            </Text>
            
            {refreshing ? (
              <View style={{ alignItems: 'center', gap: styles.utils.spacing.sm }}>
                <ActivityIndicator size="large" color={styles.tokens.color.primary} />
                <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
                  Fetching neighbor data from NC OneMap...
                </Text>
                <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                  This may take a few seconds
                </Text>
              </View>
            ) : (
              <>
                {/* Using Pressable instead of Button to avoid any web form submission issues */}
                <Pressable
                  onPress={(e) => {
                    e.preventDefault?.();
                    e.stopPropagation?.();
                    handleRefresh();
                  }}
                  disabled={refreshing}
                  style={({ pressed }) => ({
                    backgroundColor: pressed ? addColorOpacity(styles.tokens.color.primary, 0.8) : styles.tokens.color.primary,
                    paddingHorizontal: styles.utils.spacing.xl,
                    paddingVertical: styles.utils.spacing.md,
                    borderRadius: styles.utils.radius.md,
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: styles.utils.spacing.sm,
                  })}
                >
                  <Ionicons name="download-outline" size={20} color={styles.tokens.color.onPrimary} />
                  <Text style={[styles.text('base'), { 
                    color: styles.tokens.color.onPrimary, 
                    fontWeight: styles.utils.typography.weights.semibold,
                  }]}>
                    Fetch Neighbor Data
                  </Text>
                </Pressable>
                
                {parcelData.error && (
                  <View style={{
                    marginTop: styles.utils.spacing.sm,
                    padding: styles.utils.spacing.sm,
                    backgroundColor: addColorOpacity(styles.tokens.color.error, 0.1),
                    borderRadius: styles.utils.radius.sm,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: styles.utils.spacing.sm,
                  }}>
                    <Ionicons name="alert-circle" size={16} color={styles.tokens.color.error} />
                    <Text style={[styles.text('xs'), { color: styles.tokens.color.error, flex: 1 }]}>
                      {parcelData.error}
                    </Text>
                  </View>
                )}
              </>
            )}
            
            {!refreshing && (
              <View style={{ 
                flexDirection: 'row', 
                flexWrap: 'wrap', 
                justifyContent: 'center',
                gap: styles.utils.spacing.sm,
                marginTop: styles.utils.spacing.xs,
              }}>
                {[
                  { icon: 'map-outline', text: 'Tier 1-3 adjacency' },
                  { icon: 'business-outline', text: 'Owner names' },
                  { icon: 'cash-outline', text: 'Tax values' },
                  { icon: 'resize-outline', text: 'Acreage' },
                ].map((item, index) => (
                  <View key={index} style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                    backgroundColor: addColorOpacity(styles.tokens.color.surface, 0.8),
                    paddingHorizontal: styles.utils.spacing.sm,
                    paddingVertical: 4,
                    borderRadius: styles.utils.radius.sm,
                  }}>
                    <Ionicons name={item.icon as any} size={14} color={addColorOpacity(styles.tokens.color.onBackground, 0.6)} />
                    <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                      {item.text}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </Card>
      )}

      {/* Error display */}
      {parcelData.error && (
        <View
          style={{
            padding: styles.utils.spacing.md,
            backgroundColor: addColorOpacity(styles.tokens.color.error, 0.1),
            borderRadius: styles.utils.radius.md,
            flexDirection: 'row',
            alignItems: 'center',
            gap: styles.utils.spacing.sm,
          }}
        >
          <Ionicons name="warning" size={20} color={styles.tokens.color.error} />
          <Text style={[styles.text('sm'), { color: styles.tokens.color.error, flex: 1 }]}>
            {parcelData.error}
          </Text>
        </View>
      )}
    </View>
  );
}

export default ParcelsTab;

